import axios from "axios";
import React, { useEffect, useRef, useState, forwardRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useMatomo } from "@datapunt/matomo-tracker-react";

import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";

import APIs from "../../APIs";

import {
    alpha,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    Typography,
    useMediaQuery,
    TextField,
    MenuItem,
    Box,
} from "@material-ui/core";
import { SaveAlt, UnfoldLess, UnfoldMore, ExpandMore, Check } from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/styles";

import Paginator from "../../components/Paginator";
import ReportTMPL from "../../components/Report/TMPL";
import { generatePDF } from "../../components/Report/functions";
import Search from "../../components/Search";
import { useDocTitle } from "../../components/_common/DocTitle";
import Preloader from "../../components/_common/Preloader";
import GestorzyAccordion from "./GestorzyAccordion";

const regex = /(<([^>]+)>)|\t|\n|&nbsp;/gi;

const ALL_EXPANDED = process.env.REACT_APP_ALL_EXPANDED_GES
    ? JSON.parse(process.env.REACT_APP_ALL_EXPANDED_GES)
    : process.env.REACT_APP_ALL_EXPANDED
    ? JSON.parse(process.env.REACT_APP_ALL_EXPANDED)
    : false;

const DropDownIcon = forwardRef((props, ref) => (
    <IconButton ref={ref} {...props} size={"small"} component={"div"}>
        <ExpandMore color={"primary"} fontSize={"small"} />
    </IconButton>
));

const useStyles = makeStyles((theme) => ({
    bigTitle: {
        margin: theme.spacing(0, 4, 4, 4),
        textAlign: "center",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.125rem",
            fontWeight: theme.typography.fontWeightMedium,
        },
    },
    alphabet: {
        display: "flex",
        justifyContent: "space-between",
        overflow: "hidden",
    },
    listOverlay: {
        content: '""',
        position: "absolute",
        width: "100%",
        height: 1,
        bottom: -1,
        boxShadow: `0 1px 56px 64px ${theme.palette.background.paper}`,
        zIndex: 1,
    },
    listRoot: {
        paddingTop: 0,
    },
    listSection: {
        backgroundColor: "inherit",
    },
    ul: {
        backgroundColor: "inherit",
        padding: 0,
    },
    listSubheaderSticky: {
        backgroundColor: "rgb(234 237 244)",
        color: theme.palette.primary.main,
        padding: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            lineHeight: 1.4,
            fontWeight: theme.typography.fontWeightRegular,
        },
    },
    listItemSelected: {
        backgroundColor: `${alpha(theme.palette.primary.main, 0.25)}!important`,
    },
    alphabetChip: {
        backgroundColor: alpha(theme.palette.primary.light, 0.5),
        color: theme.palette.primary.contrastText,
        "&:hover": {
            backgroundColor: theme.palette.primary.main,
        },
        "&:focus": {
            backgroundColor: theme.palette.primary.main,
        },
    },
    alphabetChipAcvtive: {
        backgroundColor: theme.palette.primary.main,
    },
    alphabetAvatar: {
        backgroundColor: theme.palette.primary.main,
    },
    listItemAvatarAvatar: {
        backgroundColor: alpha(theme.palette.primary.main, 0.25),
    },
    formControl: {
        marginTop: theme.spacing(2),
        minWidth: "100%",
    },
    preRender: {
        position: "fixed",
        left: 0,
        right: "200vw",
        top: "200vh",
        width: "210mm",
    },
    pdfLoadingWrapper: {
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "rgba(255,255,255,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
    },
    searchWrapper: {
        gap: 16,
        "&, & > form": {
            display: "flex",
            alignItems: "center",
        },
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    btnReport: {
        margin: 0,
        height: 48,
        minWidth: 241,
        fontSize: "clamp(12px, 1vw, 14px)",
        "& > span": {
            marginTop: 2,
        },
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 0, 0),
            width: "100%",
            height: 36,
            paddingTop: 4,
        },
    },
    searchForm: {
        width: "100%",
        minHeight: 80,
        display: "flex",
        gap: 16,
    },
    btnSearch: {
        // margin: "16px 8px",
        margin: 0,
        height: 48,
        minWidth: 153,
        "& > span": {
            marginTop: 2,
        },
        // borderRadius: 20,
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },

        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 4, 0),
        },
    },
    alert: {
        marginTop: 32,
        marginBottom: 32,
        fontSize: "1.125rem",
        "&, & *": {
            color: "#621B16",
            // fontWeight: "bolder",
        },
    },
    paginationList: {
        opacity: 1,
        "& li": {
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.43,
        },
    },
    paginationSelect: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.43,
        paddingBottom: 6,
    },
    paginationSelectIcon: {
        top: "calc(50% - 13px)",
    },
    toolsWrapper: {
        marginTop: 16,
        padding: theme.spacing(0, 3, 0, 5.5),
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 1, 0, 0.75),
        },
    },
    formControlLabel: {
        fontSize: "clamp(12px, 1vw, 1rem)",
    },
    xsOnly: {
        display: "none",
        [theme.breakpoints.down("xs")]: {
            display: "block",
            width: "100%",
            backgroundColor: "#00000022",
        },
    },
    textField: {
        "& label": {
            fontSize: "clamp(13px, 1vw, 1rem)",
        },
        "& fieldset": {
            [theme.breakpoints.down("sm")]: {
                top: -7,
            },
        },
        "& legend": {
            fontSize: "0.835em",
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.6em",
            },
        },
        "& button": {
            top: "calc(50% - 14px)",
        },
        "& input": {
            // color: "#000!important",
            // backgroundColor: "#FFF!important",
            left: -1999,
        },
    },
    input: {
        fontSize: "clamp(13px, 1vw, 1rem)",
        paddingTop: 15,
        paddingBottom: 14,
        paddingLeft: 14,
        "& .check": {
            display: "none",
        },
    },
    label: {
        fontSize: "clamp(13px, 1vw, 1rem)",
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
    },
    inputAdornmentEnd: {},
    datePickerButtonRoot: {
        "& svg": {
            color: theme.palette.primary.main,
        },
    },
    adornedEnd: {
        paddingRight: 0,
    },
    selectMenuPaper: {
        opacity: 1,
        '& li[class*="MuiListItem-selected"]': {
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            fontWeight: 500,
        },
    },
}));

const RSIGESTORZY_Page = (props) => {
    const classes = useStyles();
    const { i18n, t } = useTranslation();
    const { trackPageView } = useMatomo();
    const cheAllRef = useRef();
    const history = useHistory();

    useDocTitle(t("pages.issuersPageTitle"));

    const savedPage = Number(localStorage.getItem("gp") || 0);
    const savedPagesPerPage = Number(localStorage.getItem("gpp") || 10);

    const isMobile = useMediaQuery("(max-width: 349.95px)");

    const [apiWait, setApiWait] = useState(true);
    const [loading, setLoading] = useState(false);
    const [gestorzy, setGestorzy] = useState([]);
    const [data, setData] = useState([]);
    const [loadingPdf, setLoadingPDF] = useState(false);
    const [searchError, setSearchError] = React.useState(false);
    // const [searchText, setSearchText] = useState("");
    const [SEARCH, setSEARCH] = useState("");
    // eslint-disable-next-line
    const [report, setReport] = useState(true);
    const [checked, setChecked] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [page, setPage] = React.useState(savedPage);
    const [rowsPerPage, setRowsPerPage] = React.useState(savedPagesPerPage);
    const [count, setCount] = React.useState(0);
    const [EXPANDED, setEXPANDED] = useState([]);
    const [QUERY, setQUERY] = useState("");
    const [SORT, setSORT] = useState("nazwa");

    /** Process url query */
    useEffect(() => {
        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        if (!Object.keys(currentQuery)?.length) {
            setApiWait(false);
            return;
        }

        if (
            Number(currentQuery?.page) !== page ||
            Number(currentQuery?.limit) !== rowsPerPage ||
            currentQuery?.search !== encodeURI(SEARCH)
        ) {
            if (Number(currentQuery?.limit) !== rowsPerPage) {
                setRowsPerPage(Number(currentQuery?.limit));
            }

            if (currentQuery?.search !== encodeURI(SEARCH)) {
                setSEARCH(currentQuery?.search ? decodeURI(currentQuery?.search) : "");
            }

            if (Number(currentQuery?.page) !== page) {
                setPage(Number(currentQuery?.page) - 1);
            }

            setApiWait(false);
        } else {
            setApiWait(false);
        }
        // eslint-disable-next-line
    }, []);

    /** get api data */
    useEffect(() => {
        if (apiWait) return;
        setSearchError(false);
        getData({ page });
        return;
        // eslint-disable-next-line
    }, [SEARCH, page, rowsPerPage, apiWait]);

    /* track page view */
    useEffect(() => {
        trackPageView();
        // eslint-disable-next-line
    }, []);

    const handleToggleChecked = (event) => {
        const _checked = event;

        const all = data?.flatMap((gestor) => {
            return gestor?.systemy?.map((system) => ({ gestor_id: gestor.id_gestor, ...system }));
        });

        setChecked(_checked ? all : []);
    };

    const handleChecked = (item) => {
        let newChecked = [];

        if (item.length) {
            newChecked =
                checked.filter((c) => c.gestor_id === item[0].gestor_id).length &&
                checked.filter((c) => c.gestor_id === item[0].gestor_id).length === item.length
                    ? checked.filter((c) => c.gestor_id !== item[0].gestor_id)
                    : [...checked.filter((c) => c.gestor_id !== item[0].gestor_id), ...item];
        } else {
            newChecked = checked.find((c) => c.id_systemu === item.id_systemu)
                ? checked.filter((c) => c.id_systemu !== item.id_systemu)
                : [...checked, item];
        }
        setChecked(newChecked);
    };

    const handleGetReport = () => {
        report && setChecked([]);
        // setReport(!report);
        if (!checked.length) return;
        console.log("Get report: ", checked);
        exportPdf({
            abouts: checked,
        });
    };

    const exportPdf = async (PDFOptions) => {
        const selectedData = orderBy(checked, ["gestor_level", "nazwa_systemu"], ["asc", "asc"]);

        let pdfData = [];

        setLoadingPDF(true);

        selectedData.forEach((item) => {
            axios
                .get(`${APIs.dataURL.rsisystemy}/${item.id_systemu}`)
                .then((result) => {
                    pdfData.push({
                        ...item,
                        ...result.data,
                    });
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    if (selectedData.length === pdfData.length) {
                        setReportData(
                            pdfData.sort(
                                (a, b) =>
                                    a.gestor_level - b.gestor_level || a.nazwa_systemu.localeCompare(b.nazwa_systemu)
                            )
                        );

                        setTimeout(() => {
                            generatePDF({
                                data: pdfData.sort(
                                    (a, b) =>
                                        a.gestor_level - b.gestor_level ||
                                        a.nazwa_systemu.localeCompare(b.nazwa_systemu)
                                ),
                                lang: i18n.language,
                            });
                            setLoadingPDF(false);
                        }, 1234);
                    }
                });
        });
        return;
    };

    const handleOnSubmitSearch = (newSearchText) => {
        setPage(0);
        setSEARCH(newSearchText);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        localStorage.setItem("gp", newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const _rpp = parseInt(event.target.value, 10);
        localStorage.setItem("gpp", _rpp);
        setRowsPerPage(_rpp);

        setPage(0);
        localStorage.setItem("gp", 0);
    };

    const handleToggleExpand = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const all = gestorzy?.map((gestor) => `obiekt-${gestor.id}`);

        let panels = EXPANDED?.length ? (EXPANDED.length === all?.length ? [] : all) : all;

        setEXPANDED(panels);
    };

    const handleOnExpand = (panel) => {
        const panels = EXPANDED?.includes(panel) ? EXPANDED?.filter((e) => e !== panel) : [...EXPANDED, panel];

        setEXPANDED(panels);
    };

    const handleSort = async (event) => {
        setSORT(event.target.value);
        const ordering = event.target.value;

        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));
        const newQuery = {
            ...currentQuery,
            ordering,
        };

        setApiWait(true);

        await Promise.all([
            // setPage(0),
            newQuery && setQUERY(newQuery),
        ]).then((res) => {
            setApiWait(false);
            setLoading(false);
        });
    };

    const getData = ({ page }) => {
        // let apiURL = `${APIs.dataURL.rsigestorzy}?limit=${rowsPerPage}&page=${page + 1}&search=${SEARCH || ""}`;
        let currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        const newQuery = new URLSearchParams(
            QUERY || { ...currentQuery, limit: rowsPerPage, page: page + 1, search: SEARCH || "" }
        ).toString();

        let apiURL = `${APIs.dataURL.rsigestorzy}?${newQuery}`;

        const newPath = `${history.location.pathname}?${newQuery}${history.location.hash}`;

        setTimeout(() => {
            history.push(newPath, { shallow: true });
        }, 100);

        setLoading(true);

        axios
            .get(apiURL)
            .then((result) => {
                const gestor_levels = {
                    "Administracja terenowa": { name: "Administracja terenowa", level: 3 },
                    "Pozostała administracja centralna": { name: "Pozostała administracja centralna", level: 2 },
                    Ministerstwo: { name: "Ministerstwo", level: 1 },
                };

                setCount(result.data.count);
                if (SEARCH && !result?.data?.count) {
                    setSearchError(t('Brak wyników wyszukiwania dla frazy "{{SEARCH}}"{{dot}}', { SEARCH, dot: "." }));
                }

                const apiData = result.data.results.map((item) => {
                    item.nazwa = item.nazwa.replace(regex, "");
                    item.systemy = item.systemy
                        .map((system) => ({
                            ...system,
                            gestor_level: gestor_levels[item.typ]?.level,
                            nazwa_systemu: system.nazwa_systemu.replace(regex, ""),
                            nazwa_systemu_skr: system.nazwa_systemu_skr
                                ? system.nazwa_systemu_skr.replace(regex, "")
                                : "",
                        }))
                        .sort((a, b) => a.nazwa_systemu.toUpperCase().localeCompare(b.nazwa_systemu.toUpperCase()));
                    return {
                        ...item,
                        gestor_level: gestor_levels[item.typ]?.level,
                    };
                });
                // .sort((a, b) => a.nazwa.toUpperCase().localeCompare(b.nazwa.toUpperCase()));

                setData(apiData);

                const _gestorzy = apiData.map((d) => ({ title: d.nazwa, id: d.id_gestor, typ: d.typ }));
                setGestorzy(_gestorzy);
                const all = _gestorzy?.map((gestor) => `obiekt-${gestor.id}`);

                if (ALL_EXPANDED) setEXPANDED(all);

                setChecked([]);
            })
            .catch((error) => {
                if (SEARCH) {
                    setSearchError(
                        t('Brak wyników wyszukiwania dla frazy "{{SEARCH}}"{{dot}}<br/><hr/>{{apiError}}', {
                            SEARCH,
                            dot: ".",
                            apiError: error.response.data.detail ? `<em>${error.response.data.detail}</em>` : "",
                        })
                    );
                } else {
                    setSearchError(error.response?.data?.detail || t("Nieznany błąd"));
                }
            })
            .finally(() => {
                setLoading(false);
                setQUERY("");
            });
    };

    const optionsSort = [
        { label: t("Nazwa od A do Z"), value: "nazwa", default: true },
        { label: t("Nazwa od Z do A"), value: "-nazwa" },
        { label: t("Data aktualizacji rosnąco"), value: "DA", date: true },
        { label: t("Data aktualizacji malejąco"), value: "DD", date: true },
    ];

    return (
        <>
            <Typography
                variant="h4"
                className={classes.bigTitle}
                color="primary"
                component={"h2"}
                id={"page-content"}
                tabIndex={0}
            >
                {t("pages.issuers")}
            </Typography>

            {!!loadingPdf && (
                <div className={classes.pdfLoadingWrapper}>
                    <img src={"/images/preloader.gif"} style={{ width: 128 }} alt={"Loading..."} />
                </div>
            )}

            {!!reportData.length && (
                <div className={classes.preRender}>
                    <ReportTMPL reportData={reportData} />
                </div>
            )}

            <Grid container>
                <Grid item xs={12} className={classes.searchWrapper}>
                    <Search
                        label={t("common.RSIGESTORZYsearchGestor")}
                        onSubmit={(newSearchText) => handleOnSubmitSearch(newSearchText)}
                        error={searchError}
                        search={SEARCH}
                        minSearchLength={process.env.REACT_APP_MIN_SEARCH_LENGTH_GES}
                    />

                    <Divider className={classes.xsOnly} />
                    <Button
                        variant={"contained"}
                        color={!checked.length ? "default" : "primary"}
                        tabIndex={0}
                        onClick={handleGetReport}
                        className={classes.btnReport}
                        disabled={!checked.length}
                        aria-label={t(
                            "Pobierz raport PDF{{dot}} Zaznacz poniżej przynajmniej jeden checkbox, aby pobrać raport",
                            { dot: "." }
                        )}
                        aria-disabled={!checked.length}
                        disableRipple
                        startIcon={<SaveAlt />}
                    >
                        {"Pobierz raport pdf"}
                    </Button>
                    <Divider className={classes.xsOnly} />
                </Grid>

                <Grid item xs={12} style={{ marginTop: 16 }}>
                    &nbsp;
                </Grid>

                <Grid item xs={12} style={{ position: "relative" /*overflow: "hidden"*/ }} id="aGrid">
                    <div className={classes.listOverlay0} />

                    {!!searchError && (
                        <Alert severity="error" className={classes.alert}>
                            <span dangerouslySetInnerHTML={{ __html: searchError }} />
                        </Alert>
                    )}

                    {!!loading && (
                        <>
                            <Preloader type="linear" />
                            <Preloader type="linear" />
                            <Preloader type="linear" />
                        </>
                    )}

                    {!!data.length && !!gestorzy.length && !loading && (
                        <Paginator
                            component="div"
                            count={count}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            queryParams={{ limit: rowsPerPage, page: page + 1, search: SEARCH }}
                            componentTopBefore={
                                <TextField
                                    id="gestorzy-sort"
                                    select
                                    label={t("Sortowanie")}
                                    value={SORT}
                                    onChange={handleSort}
                                    variant="outlined"
                                    fullWidth={!!isMobile}
                                    SelectProps={{
                                        IconComponent: DropDownIcon,
                                        MenuProps: {
                                            getContentAnchorEl: null,
                                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                                            transformOrigin: { vertical: "top", horizontal: "center" },
                                            PaperProps: {
                                                className: classes.selectMenuPaper,
                                            },
                                        },
                                        id: "gestorzy-sort-select",
                                    }}
                                    size={isMobile ? "small" : "medium"}
                                    classes={{ root: classes.textField }}
                                    inputProps={{
                                        className: classes.input,
                                    }}
                                    style={{
                                        order: isMobile ? 1 : 2,
                                        width: isMobile ? undefined : "clamp(280px, 20vw, 328px)",
                                    }}
                                    ref={(ref) => {
                                        if (ref) {
                                            const inputEl = ref.querySelector("input");

                                            if (inputEl) {
                                                setTimeout(() => {
                                                    inputEl.style.color = "#000";
                                                    inputEl.style.backgroundColor = "#FFF";
                                                    inputEl.style.opacity = 1;
                                                    inputEl.id = "gestorzy-sort";
                                                }, 500);
                                            }
                                        }
                                    }}
                                >
                                    {optionsSort.map((option) => {
                                        const _hasDateField = data?.every((d) => d.hasOwnProperty("data_aktualizacji"));

                                        return option.date && !_hasDateField ? null : (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Box
                                                    // component={option.default ? "em" : "span"}
                                                    className={classes.label}
                                                >
                                                    {option.label}
                                                    {option.value === SORT && (
                                                        <Check className="check" color="primary" />
                                                    )}
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            }
                        >
                            <div className={classes.toolsWrapper}>
                                <FormControlLabel
                                    inputRef={cheAllRef}
                                    control={
                                        <Checkbox
                                            color={"primary"}
                                            checked={uniqBy(checked, "gestor_id")?.length === gestorzy?.length}
                                            indeterminate={
                                                !!(
                                                    checked?.length &&
                                                    uniqBy(checked, "gestor_id")?.length !== gestorzy?.length
                                                )
                                            }
                                            size={isMobile ? "small" : "medium"}
                                            // onChange={handleToggleChecked}
                                            onClick={(e) => handleToggleChecked(e.target.checked)}
                                        />
                                    }
                                    label={
                                        uniqBy(checked, "gestor_id")?.length === gestorzy?.length
                                            ? t("Odznacz wszystkie na stronie")
                                            : t("Zaznacz wszystkie")
                                    }
                                    labelPlacement="end"
                                    classes={{ label: classes.formControlLabel }}
                                />
                                {/* <span/> */}

                                <FormControlLabel
                                    htmlFor="expand-ges-btn"
                                    control={
                                        <>
                                            <IconButton onClick={handleToggleExpand} component={"span"}>
                                                {EXPANDED?.length === gestorzy?.length ? (
                                                    <UnfoldLess fontSize={isMobile ? "small" : "medium"} />
                                                ) : (
                                                    <UnfoldMore fontSize={isMobile ? "small" : "medium"} />
                                                )}
                                            </IconButton>
                                            <Checkbox id="expand-ges-btn" inert style={{ display: "none" }} />
                                        </>
                                    }
                                    label={
                                        EXPANDED?.length === gestorzy?.length
                                            ? t("Zwiń wszystkie")
                                            : t("Rozwiń wszystkie")
                                    }
                                    labelPlacement="start"
                                    classes={{ label: classes.formControlLabel }}
                                    // component={"div"}
                                />
                            </div>

                            <div role="grid">
                                <List className={classes.listRoot} dense={true} id="aList">
                                    {gestorzy
                                        .filter((d) =>
                                            props.match.params.id
                                                ? d.typ.toUpperCase() === props.match.params.id.toUpperCase() ||
                                                  d.typ.toUpperCase() ===
                                                      props.match.params.id.split("_").join(" ").toUpperCase()
                                                : d
                                        )
                                        .map((gestor) => {
                                            return (
                                                <React.Fragment key={`gestor-${gestor.id}`}>
                                                    <GestorzyAccordion
                                                        gestor={gestor}
                                                        data={
                                                            data.find((d) => d.nazwa === gestor.title)
                                                                ? data
                                                                      .find((d) => d.nazwa === gestor.title)
                                                                      .systemy.map((s) => ({
                                                                          ...s,
                                                                          gestor_id: gestor.id,
                                                                      }))
                                                                : []
                                                        }
                                                        checked={checked}
                                                        onChecked={(item) => handleChecked(item)}
                                                        report={report}
                                                        search={SEARCH}
                                                        expanded={EXPANDED}
                                                        onExpand={(panel) => handleOnExpand(panel)}
                                                        isMobile={isMobile}
                                                    />
                                                </React.Fragment>
                                            );
                                        })}
                                </List>
                            </div>
                        </Paginator>
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default RSIGESTORZY_Page;
